import $ from "jquery";
import logger from "flow/logger";
import protect from "flow/protect";
import metaCache from "flow/utils/metaCache";
import * as toastr from "toastr";
import activator from "flow/utils/activator";
import eventSystem from "flow/events/eventSystem";
import flowAjax from "flow/ajax/ajax";
import manifestManager from "flow/utils/manifestManager";
import config from "flow/config";
import ajaxErrorHelper from "flow/ajax/ajaxErrorHelper";
import breadcrumbTracker from "flow/breadcrumbTracker";
import deferredD, { JQueryDeferredPlus } from "flow/promise/deferred";
import whenD from "flow/promise/when";
import sysLogger from "flow/sysLogger";
import koMapper from "flow/utils/koMapper";
import environment, { ValidEnvTypes } from "flow/configuration/environment";
import Component from "flow/Component";
import { Dictionary } from "./api/types";
import { ModuleRole } from "./entities/ModuleRole";
import { cleanHtml } from "flow/utils/cleanHtml";
import { addGlobalPersistentNotification } from "./stores/globalPersistentNotificationStore";
import { IPersistentNotification } from "./interfaces/IPersistentNotification";

function prepareMessageForClientLogging(moduleId: string, componentId: string, message: string) {
    return componentId === config.flowId
        ? "[HTML]: " + moduleId + " " + message
        : "[HTML]: " + moduleId + "[" + componentId + "] " + message;
}

export default class MyFlow {
    isMyFlow: boolean = true;
    id: string;
    baseUrl: string;
    authorizationManager: any;
    numberOfOngoingTasks: number = 0;
    useGenericAjaxErrorHandler: boolean = true;
    alwaysShowBusinessExceptionsAsInContainerNotification: boolean = false;
    component: Component;
    rootId: string;
    rootComponent: Component;

    constructor(
        public manifest: any,
        public settings: Dictionary
    ) {
        if (!this.manifest) {
            this.manifest = {};
        }

        this.id = this.manifest.id;
        this.baseUrl = this.manifest.baseUrl;

        if (!this.settings) {
            this.settings = {};
        }

        this.component = this.settings.component;

        if (this.settings.rootComponent) {
            this.rootId = this.settings.rootComponent.manifest.id;
            this.rootComponent = this.settings.rootComponent;
        } else {
            this.rootId = this.id;
            this.rootComponent = this.component;
        }

        this.authorizationManager = this.settings.authorizationManager;
    }

    error(message?: any, ...optionalParams: any[]): void {
        if (this.settings.error) {
            if (this.settings.error(message) === true) {
                return;
            }
        }
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Error"),
            message,
            sysLogger.logLevels.error
        );
        logger.error("[" + this.id + "]", message, ...optionalParams);
    }

    warn(message?: any, ...optionalParams: any[]): void {
        logger.warn("[" + this.id + "]", message, ...optionalParams);
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Warning"),
            message,
            sysLogger.logLevels.warning
        );
    }

    debug(message?: any, ...optionalParams: any[]): void {
        logger.debug("[" + this.id + "]", message, ...optionalParams);
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Debug"),
            message,
            sysLogger.logLevels.debug
        );
    }

    emergency(message: any): void {
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Emergency"),
            message,
            sysLogger.logLevels.emergency
        );
    }

    alert(message: string): void {
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Alert"),
            message,
            sysLogger.logLevels.alert
        );
    }

    critical(message: string): void {
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Critical"),
            message,
            sysLogger.logLevels.critical
        );
    }

    notice(message: string): void {
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Notice"),
            message,
            sysLogger.logLevels.notice
        );
    }

    information(message: string): void {
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Information"),
            message,
            sysLogger.logLevels.information
        );
    }

    log(message?: any, ...optionalParams: any[]): void {
        logger.log("[" + this.id + "]", message, ...optionalParams);
    }

    logObjectsDebug(...objects: any[]): void {
        logger.debug(objects[0], ...objects.slice(1));
    }

    logObjects(...objects: any[]): void {
        logger.log(objects[0], ...objects.slice(1));
    }

    logObjectsAsWarnings(...objects: any[]): void {
        logger.warn(objects[0], ...objects.slice(1));
    }

    logObjectsAsErrors(...objects: any[]): void {
        logger.error(objects[0], ...objects.slice(1));
    }

    fatal(error: Error, ...optionalParams: any[]): void {
        if (this.settings.fatal) {
            if (this.settings.fatal(error.message) === true) {
                return;
            }
        }
        sysLogger.reportToServer(
            this,
            prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Fatal"),
            error.message,
            sysLogger.logLevels.error
        );
        logger.error("[" + this.id + "]", error.stack ?? error.message, ...optionalParams);
    }

    exception(caughtException: any): boolean {
        if (this.settings.exception) {
            if (this.settings.exception(caughtException) === true) {
                return true;
            }
        }

        logger.error("[" + this.id + "]", caughtException.message);
        if (caughtException.stack) {
            logger.error(caughtException.stack);
            sysLogger.reportToServer(
                this,
                prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Exception"),
                caughtException.stack,
                sysLogger.logLevels.error
            );
        } else {
            sysLogger.reportToServer(
                this,
                prepareMessageForClientLogging(this.manifest.moduleId, this.id, "Exception"),
                caughtException,
                sysLogger.logLevels.error
            );
        }

        return true;
    }

    notifyErrorIncontainer(message: string, notificationOpaque?: boolean): void {
        this.rootComponent.notificationType(config.notificationTypes.error);
        this.rootComponent.notificationOpaque(notificationOpaque);
        this.rootComponent.notificationText(message);
    }

    notifyError(message: string, notificationOpaque?: boolean): void {
        if (
            manifestManager.isMenu(this.rootId) ||
            manifestManager.isDialog(this.rootId) ||
            (this.rootComponent && this.rootComponent.isSelected())
        ) {
            this.rootComponent.notificationType(config.notificationTypes.error);
            this.rootComponent.notificationOpaque(notificationOpaque);
            this.rootComponent.notificationText(message);
        } else {
            this.notifyErrorSystem(message);
        }
    }

    notifySuccess(message: string, notificationOpaque?: boolean): void {
        if (
            manifestManager.isMenu(this.rootId) ||
            manifestManager.isDialog(this.rootId) ||
            (this.rootComponent && this.rootComponent.isSelected())
        ) {
            this.rootComponent.notificationType(config.notificationTypes.success);
            this.rootComponent.notificationOpaque(notificationOpaque);
            this.rootComponent.notificationText(message);
        } else {
            this.notifySuccessSystem(message);
        }
    }

    notifyInfoInContainer(message: string, notificationOpaque?: boolean, notificationSticky?: boolean): void {
        this.rootComponent.notificationType(config.notificationTypes.info);
        this.rootComponent.notificationOpaque(notificationOpaque);
        this.rootComponent.notificationText(message);
        this.rootComponent.notificationSticky(notificationSticky);
    }

    notifyInfo(message: string, notificationOpaque?: boolean): void {
        if (
            manifestManager.isMenu(this.rootId) ||
            manifestManager.isDialog(this.rootId) ||
            (this.rootComponent && this.rootComponent.isSelected())
        ) {
            this.rootComponent.notificationType(config.notificationTypes.info);
            this.rootComponent.notificationOpaque(notificationOpaque);
            this.rootComponent.notificationText(message);
        } else {
            this.notifyInfoSystem(message);
        }
    }

    notifyWarning(message: string, notificationOpaque?: boolean): void {
        if (
            manifestManager.isMenu(this.rootId) ||
            manifestManager.isDialog(this.rootId) ||
            (this.rootComponent && this.rootComponent.isSelected())
        ) {
            this.rootComponent.notificationType(config.notificationTypes.warning);
            this.rootComponent.notificationOpaque(notificationOpaque);
            this.rootComponent.notificationText(message);
        } else {
            this.notifyWarningSystem(message);
        }
    }

    clearLocalNotification(): void {
        this.rootComponent.notificationText(undefined);
    }

    clearSystemNotification(): void {
        toastr.remove();
    }

    onSystemNotificationClick(): void {
        if (manifestManager.isComponent(this.id)) {
            activator.selectByInstance(this.component);
        }
    }

    notifyErrorSystem(message: string, title?: string, isSticky?: boolean): void {
        const jqObj = toastr.error(
            cleanHtml(message),
            cleanHtml(this.getTitle(title, this.translate("FlowHtml.Translations.Error"))),
            isSticky ? config.toastrConfig.stickyError : config.toastrConfig.error
        );
        jqObj.click(this.onSystemNotificationClick.bind(this));
    }

    notifySuccessSystem(message: string, title?: string, isSticky?: boolean): void {
        const jqObj = toastr.success(
            cleanHtml(message),
            cleanHtml(this.getTitle(title, this.translate("FlowHtml.Translations.Information"))),
            isSticky ? config.toastrConfig.stickySuccess : config.toastrConfig.success
        );
        jqObj.click(this.onSystemNotificationClick.bind(this));
    }

    notifyInfoSystem(message: string, title?: string, isSticky?: boolean): void {
        const jqObj = toastr.info(
            cleanHtml(message),
            cleanHtml(this.getTitle(title, this.translate("FlowHtml.Translations.Information"))),
            isSticky ? config.toastrConfig.stickyInfo : config.toastrConfig.info
        );
        jqObj.click(this.onSystemNotificationClick.bind(this));
    }

    notifyWarningSystem(message: string, title?: string, isSticky?: boolean): void {
        const jqObj = toastr.warning(
            cleanHtml(message),
            cleanHtml(this.getTitle(title, this.translate("FlowHtml.Translations.Warning"))),
            isSticky ? config.toastrConfig.stickyWarning : config.toastrConfig.warning
        );
        jqObj.click(this.onSystemNotificationClick.bind(this));
    }

    disableGenericAjaxErrorHandler(): void {
        this.useGenericAjaxErrorHandler = false;
    }

    enableBusinessExceptionsAsNotifications(): void {
        this.alwaysShowBusinessExceptionsAsInContainerNotification = true;
    }

    disableBusinessExceptionsAsNotifications(): void {
        this.alwaysShowBusinessExceptionsAsInContainerNotification = false;
    }

    addTask(promise: JQueryPromise<any>): void {
        if (this.id !== this.rootId) {
            this.rootComponent.myFlow.addTask(promise);
            return;
        }

        if (this.numberOfOngoingTasks === 0) {
            if (this.settings.startLoading) {
                protect(this, this, this.settings.startLoading)();
            }
        }
        ++this.numberOfOngoingTasks;

        promise.always(() => {
            --this.numberOfOngoingTasks;
            if (this.numberOfOngoingTasks < 1 && this.settings.stopLoading) {
                protect(this, this, this.settings.stopLoading)();
            }
        });
    }

    translate(key: string): string {
        if (key && key.indexOf(".") > -1) {
            return metaCache.lookup(key, key);
        }
        return key;
    }

    lookup(key: string, defaultValue: any): any {
        return metaCache.lookup(key, defaultValue);
    }

    activateById(id: string, viewContext?: Dictionary, selectedContainerId?: string): any {
        return activator.activateById(id, viewContext, selectedContainerId);
    }

    tryActivateById(id: string, viewContext?: Dictionary, selectedContainerId?: string): any {
        if (manifestManager.isAvailable(id)) {
            return activator.activateById(id, viewContext, selectedContainerId);
        }
        return false;
    }

    activateByIdInNewTab(id: string, viewContext?: Dictionary, selectedContainerId?: string): any {
        return activator.activateByIdInNewTab(id, viewContext, selectedContainerId);
    }

    activateByIdBesideThisContainer(id: string, viewContext?: Dictionary): any {
        return activator.activateByIdBesideExistingContainer(this.rootComponent, id, viewContext);
    }

    /**
     * @deprecated Use `activateById` instead.
     */
    activateByIdFirst(id: string, viewContext?: Dictionary): void {
        this.warn("activateByIdFirst is deprecated. Use activateById instead.");
        activator.activateById(id, viewContext, undefined);
    }

    /**
     * @deprecated Use `activateById` instead.
     */
    activateByIdEnd(id: string, viewContext?: Dictionary): void {
        this.warn("activateByIdEnd is deprecated. Use activateById instead.");
        activator.activateById(id, viewContext, undefined);
    }

    deactivateById(id: string): void {
        activator.deactivateById(id);
    }

    deactivateMyself(): void {
        activator.deactivateInstance(this.component);
    }

    reactivateWorkingContext(): void {
        activator.reactivateWorkingContext();
    }

    replaceViewContext(workingContextId: string, viewContext: Dictionary, selectedContainerId?: string): void {
        activator.replaceViewContext(workingContextId, viewContext, selectedContainerId);
    }

    selectById(id: string, filter?: (hint: any) => boolean, skipAnimation?: boolean): any {
        return activator.selectById(id, filter, skipAnimation);
    }

    selectMyself(): void {
        activator.selectByInstance(this.rootComponent);
    }

    selectFirst(skipAnimation?: boolean): void {
        activator.selectFirst(skipAnimation);
    }

    isComponentAvailable(id: string): boolean {
        return !!manifestManager.getManifestWithId(id);
    }

    isComponentActive(id: string, filter?: (hint: any) => boolean): boolean {
        return activator.isComponentActive(id, filter);
    }

    getSelectedContainerId(): string {
        return activator.getSelectedContainerId();
    }

    // ReSharper disable UnusedParameter
    openEmptyWindow(windowTitle?: string, windowFeatures?: any): Window;
    openEmptyWindow(...params: any[]): Window {
        // ReSharper restore UnusedParameter
        // Passing undefined as title instead of not passing the parameter at all
        // seems to make a difference in Firefox whether the window is opened as
        // tab or new window.
        return activator.openEmptyWindow(...params);
    }

    activateExistingExternalComponent(componentWindow: Window, id: string, viewContext: Dictionary): void {
        activator.activateExistingExternalComponent(componentWindow, id, viewContext);
    }

    showErrorDialog(title: string, message: string, showCorrelationOnly?: boolean): JQueryPromise<void> {
        return activator.showErrorDialog(this, title, message, showCorrelationOnly);
    }

    showInformationDialog(title: string, message: string): JQueryPromise<void> {
        return activator.showInformationDialog(this, title, message);
    }

    handleAjaxError(jqXhr: JQuery.jqXHR, title?: string, message?: string, showCorrelationOnly?: boolean): void {
        if (ajaxErrorHelper.isBusinessException(jqXhr)) {
            if (this.alwaysShowBusinessExceptionsAsInContainerNotification) {
                this.notifyErrorIncontainer(ajaxErrorHelper.getErrorMessageFromResponse(jqXhr, message));
            } else {
                this.notifyError(ajaxErrorHelper.getErrorMessageFromResponse(jqXhr, message));
            }
        } else if (ajaxErrorHelper.isForbidden(jqXhr)) {
            this.showErrorDialog(
                this.translate("FlowHtml.Translations.Forbidden"),
                ajaxErrorHelper.getErrorMessageFromResponse(jqXhr, message),
                showCorrelationOnly
            );
        } else if (ajaxErrorHelper.isTimeout(jqXhr)) {
            this.showErrorDialog(
                this.translate("FlowHtml.Translations.CallAborted"),
                this.translate("FlowHtml.Translations.CallTookTooLongTime"),
                showCorrelationOnly
            );
        } else {
            this.showErrorDialog(
                this.getErrorTitleGeneric(jqXhr, title),
                ajaxErrorHelper.getErrorMessageFromResponse(jqXhr, message),
                showCorrelationOnly
            );
        }
    }

    showAjaxErrorDialog = this.handleAjaxError;

    getErrorTitleGeneric(jqXhr: JQuery.jqXHR, title: string): string {
        let parsedTitle = this.translate("FlowHtml.Translations.GenericAjaxError");

        if (ajaxErrorHelper.isBadRequest(jqXhr)) {
            try {
                const responseText = JSON.parse(jqXhr.responseText);
                parsedTitle = responseText.title;
            } catch (e) {}
        }

        return title ? title : parsedTitle;
    }

    showConfirmDialog(
        title: string,
        message: string,
        buttonText1: string,
        buttonText2: string,
        buttonText3?: string
    ): JQueryPromise<boolean> {
        return activator.showConfirmDialog(this, title, message, buttonText1, buttonText2, buttonText3);
    }

    showCustomDialog<T>(id: string, viewContext?: Dictionary): JQueryPromise<T> {
        return activator.showCustomDialog<T>(this, id, viewContext);
    }

    publish(topicId: string, data?: any): void {
        eventSystem.publish(topicId, data);
    }

    subscribe(topicId: string, callback: (arg?: any) => void, isGlobal?: boolean): void {
        eventSystem.subscribe(this, topicId, callback, isGlobal);
    }

    unsubscribe(topicId: string, callback: (arg?: any) => void): void {
        eventSystem.unsubscribe(topicId, callback);
    }

    unsubscribeAll(): void {
        eventSystem.unsubscribeAllFor(this);
    }

    ajax<T>(
        url: string,
        options?: Dictionary<any>,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.ajax(this, url, options, overrideDefaultErrorHandling, overrideDefaultTimeout, abortController);
    }

    getJSONWithAnotherBaseUrl<T>(
        baseUrl: string,
        url: string,
        data?: Dictionary,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.getJSONWithAnotherBaseUrl(
            this,
            baseUrl,
            url,
            data,
            overrideDefaultErrorHandling,
            overrideDefaultTimeout,
            abortController
        );
    }

    getJSON<T>(
        url: string,
        data?: Dictionary,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.getJSON(this, url, data, overrideDefaultErrorHandling, overrideDefaultTimeout, abortController);
    }

    get<T>(
        url: string,
        data?: Dictionary,
        dataType?: string,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.get(
            this,
            url,
            data,
            dataType,
            overrideDefaultErrorHandling,
            overrideDefaultTimeout,
            abortController
        );
    }

    postJSONWithAnotherBaseUrl<T>(
        baseUrl: string,
        url: string,
        data?: Dictionary,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.postJSONWithAnotherBaseUrl(
            this,
            baseUrl,
            url,
            data,
            overrideDefaultErrorHandling,
            overrideDefaultTimeout,
            abortController
        );
    }

    postJSON<T>(
        url: string,
        data: any,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.postJSON(
            this,
            url,
            data,
            overrideDefaultErrorHandling,
            overrideDefaultTimeout,
            abortController
        );
    }

    deleteJSON<T>(
        url: string,
        data: any,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.deleteJSON(
            this,
            url,
            data,
            overrideDefaultErrorHandling,
            overrideDefaultTimeout,
            abortController
        );
    }

    putJSON<T>(
        url: string,
        data: any,
        overrideDefaultErrorHandling?: boolean,
        overrideDefaultTimeout?: number,
        abortController?: AbortController
    ): JQueryPromise<T> {
        return flowAjax.putJSON(this, url, data, overrideDefaultErrorHandling, overrideDefaultTimeout, abortController);
    }

    sendBeacon(url: string, data?: BodyInit | null): boolean {
        return flowAjax.sendBeacon(this, url, data);
    }

    requestFocus(): void {
        if (this.settings.onFocus) {
            this.settings.onFocus();
        }
    }

    getSelectedSubRolesForWebModule(webModuleId: string): ModuleRole[] {
        return manifestManager.getSelectedSubRolesForWebModule(webModuleId);
    }

    goHome(selectedContainerId?: string): void {
        activator.activateById(config.componentIds.homeContext, {}, selectedContainerId);
    }

    goBack(): void {
        activator.goBack();
    }

    gotoStep(stepIndex: number, force?: boolean): void {
        activator.gotoStep(stepIndex, force);
    }

    resizeComponent(): void {
        if (this.settings.resizeComponent) this.settings.resizeComponent();
    }

    getTitle(title: string, fallback: string): string {
        if (title) {
            return title;
        }
        if (this.component && !!this.component.title() && this.component.title().trim().length > 0) {
            return this.component.title();
        }

        return fallback;
    }

    clearBreadcrumbHistory(): void {
        breadcrumbTracker.cleanHistory();
    }

    canLeaveSweepWithUserFeedback(id: string, excludeMyself: boolean): boolean {
        return activator.canLeaveSweepWithUserFeedback(id, excludeMyself === true ? this.component : undefined);
    }

    canLeaveSweepWithoutUserFeedback(id: string, excludeMyself: boolean): boolean {
        return activator.canLeaveSweepWithoutUserFeedback(id, excludeMyself === true ? this.component : undefined);
    }

    getUiCulture(): string {
        return environment.getUiCulture();
    }

    getCulture(): string {
        return environment.getCulture();
    }

    getUserName(): string {
        return environment.getUserName();
    }

    envSubscribe(key: string, callback: (newValue: any) => void, immediate?: boolean): void {
        this.subscribe(config.topics.environmentPrefix + key, callback);
        if (immediate) {
            callback(environment.get(key));
        }
    }

    envUnsubscribe(key: string, callback: (newValue: any) => void): void {
        this.unsubscribe(config.topics.environmentPrefix + key, callback);
    }

    envGet(key: string): ValidEnvTypes {
        return environment.get(key);
    }

    deferred<T>(initCallback?: () => void): JQueryDeferredPlus<T> {
        return deferredD<T>(this, initCallback);
    }

    when(...promises: any[]): JQueryPromise<any> {
        return whenD(this, ...promises);
    }

    refreshFlow(ignoreDirtyCheck?: boolean, stayInPlace?: boolean): void {
        activator.refreshFlow(ignoreDirtyCheck, stayInPlace);
    }

    adjustViewContextInUrl(newViewContext: Dictionary): void {
        activator.adjustViewContextInUrl(newViewContext);
    }

    updateUserContext(role: string, additionalInfo: string): void {
        environment.set(environment.keys.description, role);
        environment.set(environment.keys.productName, additionalInfo);
    }

    updateNameAndRole(name: string, role: string): void {
        environment.set(environment.keys.userName, name);
        environment.set(environment.keys.description, role);
    }

    hasOngoingTasks(): boolean {
        if (this.rootId !== this.id) {
            return this.rootComponent.myFlow.hasOngoingTasks();
        }

        return this.numberOfOngoingTasks > 0;
    }

    makeObservable<T1, T2>(obj: T1, setupValidation?: (mappedObj: T2) => void): T2 {
        return koMapper.makeObservable(obj, setupValidation);
    }

    unmakeObservable<T2, T1>(mappedObj: T2 | KnockoutObservable<T2>): T1 {
        return koMapper.unmakeObservable(mappedObj);
    }

    makeObservableArray<T1, T2>(
        array: T1[],
        setupValidation?: (mappedArray: KnockoutObservableArray<T2>) => void
    ): KnockoutObservableArray<T2> {
        return koMapper.makeObservableArray(array, setupValidation);
    }

    unmakeObservableArray<T2, T1>(obsArray: KnockoutObservableArray<T2>): T1[] {
        return koMapper.unmakeObservableArray(obsArray);
    }

    clearValidationErrorsV3(object?: object): void {
        if (this.component) {
            this.component.clearValidationErrorsV3(object);
        }
    }

    getValidationErrorsV3(object?: object): KnockoutValidationErrors {
        if (this.component) {
            return this.component.getValidationErrorsV3(object);
        }

        const emptyResult = function () {
            return [] as string[];
        };
        emptyResult.showAllMessages = () => {};
        return emptyResult;
    }

    focusFirstErrorV3(formElement?: HTMLFormElement): void {
        if (this.component) {
            this.component.focusFirstErrorV3(formElement);
        }
    }

    logValidationErrorsV3(object?: object): void {
        if (this.component) {
            this.component.logValidationErrorsV3(object);
        }
    }

    isBreakpoint(name: string): boolean {
        switch (name.toLowerCase()) {
            case "xs":
                return (
                    $(".lc-responsive-state").css("width") === "575px" ||
                    $(".lc-responsive-state").css("width") === "767px"
                );
            default:
                return false;
        }
    }

    getManifestIdsByTag(tag: string): string[] {
        return manifestManager.getManifestIdsByTag(tag);
    }

    addGlobalPersistentNotification(notification: IPersistentNotification): void {
        addGlobalPersistentNotification(notification);
    }
}
